import { useState } from 'react'
import { useLogin } from '@/v2/hooks/auth/useAuthentication'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useAuthContext } from '@/v2/contexts/AuthContext'

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  password: yup.string().required('Password is required'),
})

export const useLoginForm = () => {
  const navigate = useNavigate()
  const { login, isLoading } = useLogin()
  const { onOpenSnackbar } = useSnackbarContext()
  const { setUser, setIsLoggedIn } = useAuthContext()

  const [values, setValues] = useState({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState({})

  const {
    register,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(loginSchema),
    defaultValues: values,
  })

  const handleLogin = async () => {
    const credentials = {
      session: {
        email: values.email,
        password: values.password,
      },
    }
    await login(credentials, {
      onSuccess: data => {
        setUser(data)
        setIsLoggedIn(true)
        navigate('/v2')
      },
      onError: error => {
        onOpenSnackbar(`${error}`, 'error')
      },
    })
  }

  const handleSubmitForm = async () => {
    try {
      // Validate the values with Yup.
      await loginSchema.validate(values, { abortEarly: false })
      setErrors({})
      await handleLogin()
    } catch (validationError) {
      const fieldErrors = {}
      if (validationError.inner && validationError.inner.length > 0) {
        validationError.inner.forEach(err => {
          if (!fieldErrors[err.path]) {
            fieldErrors[err.path] = err.message
          }
        })
      } else {
        // Fallback in case there's a single error
        fieldErrors[validationError.path] = validationError.message
      }
      setErrors(fieldErrors)
    }
  }

  return {
    values,
    errors,
    isLoading,
    isSubmitting,
    register,
    setValues,
    handleSubmitForm,
  }
}
